import { ExecutionPlanVersionChoice } from "@/components/base/ExecutionPlanVersionChoice";
import { useApiError, useTranslation } from "@/hooks";
import {
	useGetDashboardCommon,
	useGetSettings,
	useUpdateSettings,
} from "@/services";
import {
	INTENT_PERMISSIONS,
	currentUserAtom,
	userIntentAtom,
} from "@/store/login";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	ErrorBoundary,
	Form,
	Input,
	SmartSelect,
	Switch,
	Typography,
} from "@st-marketplace/ui";
import { t as translate } from "i18next";
import { useAtomValue } from "jotai";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
	InputSkeleton,
	SettingsAccountCard,
	SettingsCard,
	SettingsField,
	SettingsOrderRangeFields,
	SettingsPasswordCard,
	SettingsRow,
	SettingsStockVisibility,
	SettingsSubheading,
	TwoInputSkeleton,
} from "./components";
import { normalizeSettings } from "./utils";

// ========================================================
// Schemas / types
// ========================================================
const settingsSchema = z
	.object({
		openForOrders: z.boolean(),
		fromOrderRange: z.coerce.number().min(1).nullable(),
		toOrderRange: z.coerce.number().min(1).nullable(),
		walletAddress: z.string().nullable(),
		sellPrice: z.coerce.number().nullable(),
		reservedBalance: z.coerce.number().min(0).nullable(),
		maxSender: z.coerce.number().min(1).nullable(),
		stockRatioPercentage: z.coerce.number().min(0).max(100).nullable(),
		globalSenderPriority: z.string().nullable(),
		snipeCooldownHours: z.coerce.number().int().min(0).max(720).nullable(),
		executionPlanVersion: z.enum(["v1", "v2"]),
	})
	.refine(
		(d) =>
			d.fromOrderRange == null ||
			d.toOrderRange == null ||
			d.fromOrderRange <= d.toOrderRange,
		{
			path: ["toOrderRange"],
			message: translate("validation.greaterOrEqualFrom"),
		},
	);

export type SettingsFormData = z.infer<typeof settingsSchema>;

// ========================================================
// Selling settings (marketplace + senders)
// ========================================================
const SellingSettings: React.FC = () => {
	const { t } = useTranslation();
	const { getErrorInterface } = useApiError();
	const user = useAtomValue(currentUserAtom);
	const userIntent = useAtomValue(userIntentAtom);

	const updateSettingsMutation = useUpdateSettings();

	const priorityItems = [
		{ value: "EXCLUDED", label: t("enums.sender_priority.excluded") },
		{ value: "LOW", label: t("enums.sender_priority.low") },
		{ value: "MEDIUM", label: t("enums.sender_priority.medium") },
		{ value: "HIGH", label: t("enums.sender_priority.high") },
		{ value: "VERY_HIGH", label: t("enums.sender_priority.very_high") },
	];

	const {
		data: settingsData,
		isLoading,
		error: settingsError,
		refetch: refetchSettings,
	} = useGetSettings({
		path: { user_id: user?.id || "" },
	});
	const {
		data: dashboardData,
		isLoading: isDashboardLoading,
		error: dashboardError,
		refetch: refetchDashboard,
	} = useGetDashboardCommon({ query: { user_id: user?.id } });

	const form = useForm<SettingsFormData>({
		resolver: zodResolver(settingsSchema),
		defaultValues: {
			openForOrders: true,
			fromOrderRange: null,
			toOrderRange: null,
			walletAddress: null,
			sellPrice: null,
			reservedBalance: null,
			maxSender: null,
			stockRatioPercentage: null,
			globalSenderPriority: "MEDIUM",
			snipeCooldownHours: null,
			executionPlanVersion: "v2",
		},
	});

	const stockRatioPercentage = form.watch("stockRatioPercentage");

	useEffect(() => {
		if (!settingsData) return;
		form.reset(normalizeSettings(settingsData), {
			keepDirtyValues: true,
			keepErrors: true,
		});
	}, [settingsData, form]);

	const onSubmit = form.handleSubmit((data: SettingsFormData) => {
		updateSettingsMutation.mutate({
			path: { user_id: user?.id || "" },
			body: {
				open_for_orders: data.openForOrders,
				minimum_to_order: data.fromOrderRange
					? data.fromOrderRange * 1_000
					: null,
				maximum_to_order: data.toOrderRange ? data.toOrderRange * 1_000 : null,
				wallet: data.walletAddress,
				default_sell_price: data.sellPrice,
				default_reserved_balance:
					data.reservedBalance != null ? data.reservedBalance * 1_000 : null,
				maximum_sender_for_proxy: data.maxSender,
				stock_ratio_percentage: data.stockRatioPercentage,
				global_sender_priority: data.globalSenderPriority as
					| "EXCLUDED"
					| "LOW"
					| "MEDIUM"
					| "HIGH"
					| "VERY_HIGH"
					| null,
				snipe_cooldown_hours: data.snipeCooldownHours,
				execution_plan_version: data.executionPlanVersion,
			},
		});
	});

	const thousandsSuffix = <span>{t("signs.thousands").toUpperCase()}</span>;
	const settingsUnavailable = !!settingsError && !settingsData;
	const loadError = settingsUnavailable
		? getErrorInterface(settingsError)
		: undefined;
	if (loadError) {
		return (
			<ErrorBoundary
				isLoading={false}
				error={loadError}
				onReset={refetchSettings}
			>
				<div />
			</ErrorBoundary>
		);
	}

	return (
		<Form {...form}>
			<form noValidate onSubmit={onSubmit} className="flex flex-col gap-6">
				<SettingsCard
					layout="columns"
					title={t("settings.marketplace.title")}
					footer={
						<Button
							type="submit"
							disabled={isLoading || !settingsData}
							loading={updateSettingsMutation.isPending}
						>
							{t("settings.saveButton")}
						</Button>
					}
				>
					<SettingsField
						control={form.control}
						name="openForOrders"
						className="xl:col-start-1 xl:row-start-1"
						title={t("settings.marketplace.salesAvailability")}
						render={(field) => (
							<div className="flex items-center gap-3">
								<Switch
									checked={!!field.value}
									onCheckedChange={field.onChange}
									disabled={isLoading}
								/>
								<Typography variant="body-sm">
									{field.value
										? t("settings.marketplace.enabled")
										: t("settings.marketplace.disabled")}
								</Typography>
							</div>
						)}
					/>

					<SettingsField
						control={form.control}
						name="stockRatioPercentage"
						className="xl:col-start-1 xl:row-span-2 xl:row-start-2"
						title={t("settings.marketplace.stockRatioPercentage")}
						render={(field) =>
							isLoading ? (
								<InputSkeleton />
							) : (
								<div className="flex flex-col gap-2">
									<Input
										type="number"
										min={0}
										max={100}
										placeholder={t(
											"settings.marketplace.stockRatioPercentageInputLabel",
										)}
										suffix={<span>%</span>}
										value={field.value ?? ""}
										onChange={(e) => {
											const { value } = e.target;
											if (value === "") {
												form.setValue("stockRatioPercentage", null, {
													shouldDirty: true,
												});
												return;
											}
											form.setValue(
												"stockRatioPercentage",
												Math.max(0, Math.min(100, Number(value))),
												{ shouldDirty: true, shouldValidate: true },
											);
										}}
									/>
									{userIntent !== "BUY" && dashboardError ? (
										<Button
											type="button"
											variant="text"
											color="primary"
											onClick={() => refetchDashboard()}
										>
											{t("error.tryAgain")}
										</Button>
									) : userIntent !== "BUY" &&
										(isDashboardLoading || dashboardData) ? (
										<SettingsStockVisibility
											actualStock={
												dashboardData?.own_available_coins.value ?? 0
											}
											ratio={stockRatioPercentage}
											loading={isLoading || isDashboardLoading}
										/>
									) : null}
								</div>
							)
						}
					/>

					<SettingsRow
						className="xl:col-start-2 xl:row-start-1"
						title={t("settings.marketplace.orderRange")}
					>
						{isLoading ? (
							<TwoInputSkeleton />
						) : (
							<SettingsOrderRangeFields<SettingsFormData>
								control={form.control}
								fromName="fromOrderRange"
								toName="toOrderRange"
								fromLabel={t("settings.marketplace.from")}
								toLabel={t("settings.marketplace.to")}
								unitLabel={t("signs.thousands").toUpperCase()}
							/>
						)}
					</SettingsRow>

					<SettingsField
						control={form.control}
						name="walletAddress"
						className="xl:col-start-2 xl:row-start-2"
						title={t("settings.marketplace.walletTitle")}
						description={t("settings.marketplace.walletSubtitle")}
						render={(field) =>
							isLoading ? (
								<InputSkeleton />
							) : (
								<Input
									{...field}
									value={field.value ?? ""}
									ltr
									placeholder={t("settings.marketplace.walletInputLabel")}
								/>
							)
						}
					/>

					<SettingsField
						control={form.control}
						name="sellPrice"
						className="xl:col-start-2 xl:row-start-3"
						title={t("settings.sellPrice.title")}
						description={t("settings.sellPrice.subtitle")}
						render={(field) =>
							isLoading ? (
								<InputSkeleton />
							) : (
								<Input
									type="number"
									placeholder={t("settings.sellPrice.inputLabel")}
									value={field.value ?? ""}
									onChange={field.onChange}
								/>
							)
						}
					/>
					<SettingsSubheading title={t("settings.senders.title")} />

					<SettingsField
						control={form.control}
						name="reservedBalance"
						className="xl:col-start-1 xl:row-start-5"
						title={t("settings.senders.reserveBalanceTitle")}
						description={t("settings.senders.reserveBalanceSubtitle")}
						render={(field) =>
							isLoading ? (
								<InputSkeleton />
							) : (
								<Input
									type="number"
									placeholder={t("settings.senders.reserveBalanceInputLabel")}
									value={field.value ?? ""}
									onChange={field.onChange}
									suffix={thousandsSuffix}
								/>
							)
						}
					/>

					<SettingsField
						control={form.control}
						name="maxSender"
						className="xl:col-start-1 xl:row-start-6"
						title={t("settings.senders.maxSenderTitle")}
						description={t("settings.senders.maxSenderSubtitle")}
						render={(field) =>
							isLoading ? (
								<InputSkeleton />
							) : (
								<Input
									type="number"
									placeholder={t("settings.senders.maxSenderInputLabel")}
									value={field.value ?? ""}
									onChange={field.onChange}
								/>
							)
						}
					/>

					<SettingsField
						control={form.control}
						name="snipeCooldownHours"
						className="xl:col-start-2 xl:row-start-5"
						title={t("settings.senders.snipeCooldownTitle")}
						description={t("settings.senders.snipeCooldownSubtitle")}
						render={(field) =>
							isLoading ? (
								<InputSkeleton />
							) : (
								<Input
									type="number"
									min={0}
									max={720}
									placeholder={t("settings.senders.snipeCooldownInputLabel")}
									value={field.value ?? ""}
									onChange={field.onChange}
									suffix={<span>{t("time.hour")}</span>}
								/>
							)
						}
					/>

					<SettingsField
						control={form.control}
						name="globalSenderPriority"
						className="xl:col-start-2 xl:row-start-6"
						title={t("settings.senders.globalPriorityTitle")}
						description={t("settings.senders.globalPriorityHelper")}
						render={(field) =>
							isLoading ? (
								<InputSkeleton />
							) : (
								<SmartSelect
									className="w-full"
									items={priorityItems}
									value={field.value ?? undefined}
									onChange={field.onChange}
									placeholder={t("settings.senders.globalPriorityLabel")}
								/>
							)
						}
					/>

					<SettingsField
						control={form.control}
						name="executionPlanVersion"
						className="xl:col-span-2 xl:row-start-7"
						title={t("executionPlanVersion.title")}
						description={t("executionPlanVersion.description")}
						render={(field) =>
							isLoading ? (
								<TwoInputSkeleton />
							) : (
								<ExecutionPlanVersionChoice
									value={field.value}
									onChange={field.onChange}
									className="xl:grid-cols-2"
								/>
							)
						}
					/>
				</SettingsCard>
			</form>
		</Form>
	);
};

// ========================================================
// Page
// ========================================================
const Settings: React.FC = () => {
	const { t } = useTranslation();
	const userIntent = useAtomValue(userIntentAtom);

	const canSell =
		!userIntent || INTENT_PERMISSIONS[userIntent].navigation.settings;

	return (
		<div className="flex w-full flex-col gap-6 pb-10">
			<header>
				<Typography variant="title-md" as="h1">
					{t("settings.title")}
				</Typography>
				<Typography variant="body-sm" className="mt-1 text-secondary-text-100">
					{t("settings.description")}
				</Typography>
			</header>

			{/* account cards are secondary: a narrow side column next to the selling settings */}
			<div className="grid grid-cols-12 items-start gap-6">
				{canSell && (
					<div className="col-span-12 lg:col-span-8 2xl:col-span-9">
						<SellingSettings />
					</div>
				)}
				<div
					className={
						canSell
							? "col-span-12 grid gap-6 md:grid-cols-2 lg:col-span-4 lg:grid-cols-1 2xl:col-span-3"
							: "col-span-12 grid gap-6 md:grid-cols-2 xl:col-span-8"
					}
				>
					<SettingsAccountCard />
					<SettingsPasswordCard />
				</div>
			</div>
		</div>
	);
};

export default Settings;
